import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from "react-router-dom";

import { useAuth } from "@/global/AuthContext";
import type { CrumbHandle } from "@/hooks/use-crumbs";
import i18n from "@/i18n";
import AppLayout, { type AppLayoutHandle } from "@/layout/AppLayout";
import RootErrorBoundary from "@/layout/RootErrorBoundary";
import Dashboard from "@/views/dashboard";
import Demand from "@/views/demand";
import ExecutionsPage from "@/views/executions";
import Insights from "@/views/insights";
import ItemsDetail from "@/views/items/detail";
import { ItemCrumb } from "@/views/items/detail/ItemCrumb";
import ItemsList from "@/views/items/index";
import LoginView from "@/views/login";
import PlanningPoliciesPage from "@/views/planning-policies";
import PlanningRunsList from "@/views/planning/PlanningRunsList";
import PlanningWorkspace from "@/views/planning/workspace/PlanningWorkspace";
import ProfilePage from "@/views/profile";
import CleansingSettings from "@/views/settings/configuration/Cleansing";
import CompanySettings from "@/views/settings/configuration/Company";
import ForecastingSettings from "@/views/settings/configuration/Forecasting";
import PlanningSettings from "@/views/settings/configuration/Planning";
import IntegrationsPage from "@/views/settings/integrations";
import SettingsLayout from "@/views/settings/SettingsLayout";
import SuppliersPage from "@/views/settings/suppliers";
import TagsPage from "@/views/settings/tags";
import UsersPage from "@/views/settings/users";

// Helper so route definitions stay type-checked against CrumbHandle without
// per-route casts. `i18n.t()` calls are evaluated at render time (each crumb
// is a function), and consumers subscribe to language changes via useCrumbs().
const crumb = (
  handle: CrumbHandle & AppLayoutHandle,
): CrumbHandle & AppLayoutHandle => handle;

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route path="/login" element={<LoginView />} />

      <Route element={<RequireAuth />}>
        <Route path="" element={<AppLayout />}>
          <Route
            path="items"
            handle={crumb({ crumb: () => i18n.t("items.title") })}
          >
            <Route
              path=":id"
              element={<ItemsDetail />}
              handle={crumb({
                crumb: (_data, params) => <ItemCrumb params={params} />,
              })}
            />
            <Route index element={<ItemsList />} />
          </Route>
          <Route index element={<Dashboard />} />
          <Route
            path="insights"
            element={<Insights />}
            handle={crumb({ crumb: () => i18n.t("insights.title") })}
          />
          <Route
            path="explore"
            element={<Demand />}
            handle={crumb({ crumb: () => i18n.t("mainNavigation.explore") })}
          />
          <Route
            path="planning"
            handle={crumb({
              crumb: () => i18n.t("mainNavigation.groups.planning"),
            })}
          >
            <Route index element={<Navigate to="/planning/runs" replace />} />
            <Route
              path="runs"
              handle={crumb({ crumb: () => i18n.t("mainNavigation.runs") })}
            >
              <Route index element={<PlanningRunsList />} />
              <Route
                path=":runId"
                element={<PlanningWorkspace />}
                handle={crumb({
                  layout: "workspace",
                  crumb: (_data, params) =>
                    i18n.t("planningRuns.detail.title", { id: params.runId }),
                })}
              />
            </Route>
            <Route
              path="executions"
              element={<ExecutionsPage />}
              handle={crumb({ crumb: () => i18n.t("executions.title") })}
            />
            <Route
              path="policies"
              element={<PlanningPoliciesPage />}
              handle={crumb({ crumb: () => i18n.t("mainNavigation.policies") })}
            />
          </Route>
          <Route
            path="profile"
            element={<ProfilePage />}
            handle={crumb({ crumb: () => i18n.t("profile.title") })}
          />
          <Route
            path="settings"
            element={<SettingsLayout />}
            handle={crumb({ crumb: () => i18n.t("settings.title") })}
          >
            <Route
              index
              element={<Navigate to="/settings/forecasting" replace />}
            />
            <Route
              path="forecasting"
              element={<ForecastingSettings />}
              handle={crumb({
                crumb: () => i18n.t("settings.rail.forecasting"),
              })}
            />
            <Route
              path="cleansing"
              element={<CleansingSettings />}
              handle={crumb({ crumb: () => i18n.t("settings.rail.cleansing") })}
            />
            <Route
              path="company"
              element={<CompanySettings />}
              handle={crumb({ crumb: () => i18n.t("settings.rail.company") })}
            />
            <Route
              path="planning"
              element={<PlanningSettings />}
              handle={crumb({ crumb: () => i18n.t("settings.rail.planning") })}
            />
            <Route
              path="users"
              element={<UsersPage />}
              handle={crumb({ crumb: () => i18n.t("settings.rail.users") })}
            />
            <Route
              path="tags"
              element={<TagsPage />}
              handle={crumb({ crumb: () => i18n.t("settings.rail.tags") })}
            />
            <Route
              path="suppliers"
              element={<SuppliersPage />}
              handle={crumb({ crumb: () => i18n.t("settings.rail.suppliers") })}
            />
            <Route
              path="integrations"
              element={<IntegrationsPage />}
              handle={crumb({
                crumb: () => i18n.t("settings.rail.integrations"),
              })}
            />
          </Route>
        </Route>
      </Route>
    </Route>,
  ),
);
export default router;

function RequireAuth() {
  const auth = useAuth();
  if (!auth.isLoggedIn) {
    return <Navigate to="/login" />;
  }
  return <Outlet />;
}
